import type React from "react";
import {useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Slider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {Close, ChevronRight} from "@mui/icons-material";
import type {
    SegmentationAxes,
    SegmentationScore,
    SegmentationType,
} from "~/pages/supplier-360/types/Supplier360Types";
import {useOverrideSupplierSegmentationMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useOverrideSupplierSegmentationMutation";
import {COLORS} from "~/style/colors-ts";
import {SEGMENT_AXES, SEGMENT_AXES_DEFAULT} from "./segmentationAxes";
import {
    SEGMENT_COLOR,
    SEGMENT_DESCRIPTION,
    SEGMENT_LABEL,
    SEGMENT_MATRIX_ORDER,
} from "./kraljicSegments";
import {SegmentTag} from "./SegmentTag";

/** The design asks for "at least a short sentence" on an override and holds the button until it has one. */
const MIN_REASON_LENGTH = 12;

type SegmentationScoreDialogProps = {
    entityId: number;
    supplierName: string;
    /** `null` when the supplier has no score yet — the dialog then writes the first one. */
    segmentation: SegmentationScore | null;
    onClose: () => void;
};

export const SegmentationScoreDialog: React.FC<SegmentationScoreDialogProps> = ({
                                                                                    entityId,
                                                                                    supplierName,
                                                                                    segmentation,
                                                                                    onClose,
                                                                                }) => {
    const isFirstScore = segmentation === null;

    const [axes, setAxes] = useState<SegmentationAxes>(() => (segmentation
        ? {
            spend_value: segmentation.spend_value,
            supply_risk: segmentation.supply_risk,
            switching_difficulty: segmentation.switching_difficulty,
            substitutes: segmentation.substitutes,
            business_criticality: segmentation.business_criticality,
        }
        : SEGMENT_AXES_DEFAULT));
    const [segment, setSegment] = useState<SegmentationType | null>(segmentation?.segment ?? null);
    // Set once the axes and the segment are agreed — the reason step then justifies replacing the score.
    const [confirming, setConfirming] = useState(false);
    const [reason, setReason] = useState('');
    const [reasonError, setReasonError] = useState(false);

    const {mutate: save, isPending} = useOverrideSupplierSegmentationMutation();

    const closeIfIdle = () => {
        if (!isPending) onClose();
    };

    const apply = () => {
        if (!segment) return;
        if (isFirstScore) {
            save({...axes, entityId, segment}, {onSuccess: onClose});
            return;
        }
        setConfirming(true);
    };

    const saveOverride = () => {
        if (!segment) return;
        save({...axes, entityId, segment, reason: reason.trim()}, {onSuccess: onClose});
    };

    return (
        <Dialog
            open
            onClose={closeIfIdle}
            fullWidth
            maxWidth="sm"
            aria-labelledby="segmentation-score-title"
            slotProps={{paper: {sx: {maxWidth: 660}}}}
        >
            <DialogTitle id="segmentation-score-title" sx={{pr: 6, fontSize: 16}}>
                {confirming
                    ? 'Justify segment change'
                    : `${isFirstScore ? 'Set segmentation' : 'Re-score'} — ${supplierName}`}
                <IconButton
                    onClick={closeIfIdle}
                    title="Close"
                    size="small"
                    sx={{position: 'absolute', top: 12, right: 12}}
                >
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            {confirming && segmentation
                ? (
                    <DialogContent>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>
                            {`${supplierName} is currently recorded as ${SEGMENT_LABEL[segmentation.segment]}. `}
                            Changing it replaces the recorded segment.
                        </Typography>

                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={2}
                            sx={{
                                mt: 1.5,
                                p: 1.5,
                                border: 1,
                                borderColor: 'grey.100',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Stack gap={0.5}>
                                <Typography variant="caption" color="text.secondary">Current</Typography>
                                <SegmentTag segment={segmentation.segment} />
                            </Stack>
                            <ChevronRight fontSize="small" htmlColor={COLORS.grey[700]} />
                            <Stack gap={0.5}>
                                <Typography variant="caption" color="text.secondary">New</Typography>
                                {segment && <SegmentTag segment={segment} />}
                            </Stack>
                        </Stack>

                        <TextField
                            autoFocus
                            fullWidth
                            multiline
                            minRows={3}
                            sx={{mt: 1.5}}
                            value={reason}
                            disabled={isPending}
                            placeholder="Why is this the right segment for this supplier?"
                            error={reasonError}
                            helperText="Recorded on the audit trail with your name and timestamp."
                            onChange={e => {
                                setReason(e.target.value);
                                setReasonError(false);
                            }}
                        />
                    </DialogContent>
                )
                : (
                    <DialogContent>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
                            Rate the five axes and choose the segment. Ratings are recorded for comparison —
                            the segment is always your call.
                        </Typography>

                        <Stack gap={1.5}>
                            {SEGMENT_AXES.map(a => (
                                <Stack key={a.key} direction="row" alignItems="center" gap={2}>
                                    <Stack sx={{width: 180, flexShrink: 0}}>
                                        <Typography fontSize={12.5} color="grey.800">{a.label}</Typography>
                                        <Typography fontSize={11} color="text.secondary">
                                            {a.low} → {a.high}
                                        </Typography>
                                    </Stack>
                                    <Slider
                                        min={1}
                                        max={5}
                                        step={0.5}
                                        color="info"
                                        value={axes[a.key]}
                                        aria-label={a.label}
                                        disabled={isPending}
                                        onChange={(_, value) => setAxes(prev => ({...prev, [a.key]: value as number}))}
                                    />
                                    <Typography variant="body2" fontWeight={700} sx={{width: 44, textAlign: 'right', flexShrink: 0}}>
                                        {axes[a.key]}
                                        <Typography component="span" variant="caption" color="text.secondary">/5</Typography>
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 2.5, mb: 1}}>
                            Segment
                        </Typography>
                        <Box
                            role="radiogroup"
                            aria-label="Segment"
                            sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'}, gap: 1}}
                        >
                            {SEGMENT_MATRIX_ORDER.map(k => {
                                const picked = segment === k;
                                return (
                                    <Box
                                        key={k}
                                        component="button"
                                        type="button"
                                        role="radio"
                                        aria-checked={picked}
                                        disabled={isPending}
                                        onClick={() => setSegment(k)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: 0.25,
                                            p: 1.25,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            borderRadius: 1,
                                            border: 1,
                                            borderColor: picked ? SEGMENT_COLOR[k].main : 'grey.200',
                                            bgcolor: picked ? SEGMENT_COLOR[k].tint : 'transparent',
                                            '&:hover:not(:disabled)': {borderColor: SEGMENT_COLOR[k].main},
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" gap={0.75} sx={{color: SEGMENT_COLOR[k].main}}>
                                            <Typography variant="body2" fontWeight={600} color="inherit">
                                                {SEGMENT_LABEL[k]}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary">
                                            {SEGMENT_DESCRIPTION[k]}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </DialogContent>
                )}

            <DialogActions sx={{px: 2.5, pb: 2.5}}>
                <Button variant="outlined" disabled={isPending} onClick={confirming ? () => setConfirming(false) : onClose}>Cancel</Button>
                {confirming ? (
                    <Button
                        variant="contained"
                        disabled={isPending}
                        endIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
                        onClick={saveOverride}
                    >
                        Save override
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        disabled={!segment || isPending}
                        endIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
                        onClick={apply}
                    >
                        {isFirstScore ? 'Set segment' : 'Apply'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
